import { useParams, Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money } from "@/lib/api";
import { Panel, RagBadge, useLoad, Loading, Table } from "@/components/common";

export default function ReportView() {
  const { id } = useParams();
  const { data: r, error } = useLoad(() => api.get(`/reports/${id}`).then((x) => x.data), [id]);
  if (!r) return <Loading error={error} />;
  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="status-report-view">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">Weekly status report · {r.project_code}</div>
          <h1 className="text-3xl font-semibold">{r.project_name}</h1>
          <div className="text-sm text-slate-500">Week of {r.week} · {r.phase} · published by {r.author}</div>
        </div>
        <div className="no-print flex gap-2">
          <Link to={`/projects/${r.project_id}?tab=reports`}><Button variant="outline">Back</Button></Link>
          <Button onClick={() => window.print()} data-testid="print-report-btn"><Printer className="mr-1 h-4 w-4" />PDF / Print</Button>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">{["overall", "cost", "schedule", "risk", "compliance"].map((k) => (
        <div key={k} className="rounded-lg border bg-white p-3 text-center"><div className="mb-1 text-xs uppercase text-slate-500">{k}</div><RagBadge value={r.rag?.[k]} /></div>))}</div>
      <Panel title="Summary"><p className="whitespace-pre-wrap text-sm">{r.summary}</p></Panel>
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Achievements this period"><p className="whitespace-pre-wrap text-sm">{r.achievements || "—"}</p></Panel>
        <Panel title="Next period"><p className="whitespace-pre-wrap text-sm">{r.next_period || "—"}</p></Panel>
      </div>
      {r.decisions_needed && <Panel title="Decisions needed"><p className="text-sm">{r.decisions_needed}</p></Panel>}
      {r.metrics && <Panel title="Financials"><div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>Budget <b className="block">{money(r.metrics.budget, r.metrics.currency || r.currency)}</b></div><div>Actual <b className="block">{money(r.metrics.actual, r.metrics.currency || r.currency)}</b></div>
        <div>EAC <b className="block">{money(r.metrics.eac, r.metrics.currency || r.currency)}</b></div><div>Variance <b className="block">{r.metrics.variance_pct}%</b></div></div></Panel>}
      <Panel title="Milestones"><Table rows={r.milestones || []} cols={[{ k: "name", h: "Milestone" }, { k: "baseline_due", h: "Baseline" }, { k: "due", h: "Forecast" }, { k: "status", h: "Status" }]} /></Panel>
      <Panel title="Top RAID"><Table rows={r.top_raid || []} cols={[{ k: "ref", h: "Ref" }, { k: "title", h: "Item" }, { k: "score", h: "Score" }]} /></Panel>
      <Panel title="Overdue documents on Basecamp" testid="report-overdue-docs"><Table rows={r.overdue_documents || []} empty="None overdue" cols={[{ k: "title", h: "Document" }, { k: "gate", h: "Gate", r: (x) => x.gate || "one-off" }, { k: "due", h: "Due" }, { k: "days_overdue", h: "Working days late" }, { k: "owner_name", h: "Responsible" }]} /></Panel>
    </div>
  );
}
